import React from 'react';
import classNames from 'classnames';
import { UseProviderStatusResult } from '../../../hooks/health/use-provider-status';
import { isProviderDegraded, statusLabel } from '../provider-status';
import { formatAgo } from '../format';

interface Props {
	status: UseProviderStatusResult;
}

/**
 * Vendor status, limited to the components this app depends on. Vonage's page
 * indicator covers their whole API surface, so an SMS incident would otherwise
 * report the page degraded while every video component is fine.
 */
export default function ProviderStatusPanel({ status }: Props) {
	const { providers, fetchedAt, refresh } = status;
	const degraded = providers.filter(isProviderDegraded);

	return (
		<section className='health-panel'>
			<header className='health-panel__header'>
				<div>
					<h3 className='health-panel__title'>Provider status</h3>
					<span className='health-panel__source'>vendor status pages</span>
				</div>
				<div className='health-panel__status'>
					<span className={classNames('health-pill', degraded.length ? 'health-pill--degraded' : 'health-pill--ok')}>
						{degraded.length ? `${degraded.length} degraded` : 'all operational'}
					</span>
					<button type='button' className='health-link-button' onClick={refresh}>
						{fetchedAt ? `checked ${formatAgo(fetchedAt)}` : 'checking…'}
					</button>
				</div>
			</header>

			<div className='health-panel__body'>
				{!providers.length && <p className='health-panel__note'>Checking provider status…</p>}

				<div className='health-chips'>
					{providers.map((provider) => (
						<a
							key={provider.key}
							className={classNames('health-chip health-chip--link', {
								'health-chip--on': provider.status === 'operational',
								'health-chip--warn': isProviderDegraded(provider),
							})}
							href={provider.url}
							target='_blank'
							rel='noreferrer'
						>
							{provider.label}
							<span className='health-chip__value'>
								{provider.status === 'unknown' ? 'unreachable' : statusLabel(provider.status)}
							</span>
						</a>
					))}
				</div>

				{providers.map((provider) => (
					(provider.affected.length || provider.incidents.length) ? (
						<div className='health-provider' key={provider.key}>
							<strong>{provider.label}</strong>
							{provider.affected.map((component) => (
								<div className='health-error-text' key={component.name}>
									{component.name}: {statusLabel(component.status)}
								</div>
							))}
							{provider.incidents.map((incident) => (
								<div className='health-subtle' key={incident.name}>
									{incident.name} ({incident.impact})
								</div>
							))}
						</div>
					) : null
				))}
			</div>
		</section>
	);
}
